import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';
import { formatRoleLabel } from '../utils/roles';
// Shared GMS math (no Recharts in chartUtils, so this stays out of the chart chunk).
import { normalizeBoatClass, splitFor, percentGms, gmsPaceForRow } from './analytics/chartUtils';
import HeadshotUploader from './HeadshotUploader';
import AthleteHeadshot from './AthleteHeadshot';
import { parseTwoKTime, formatTwoKTime } from '../utils/twoKTime';
import { PrognosticBadges } from './PrognosticMetrics';
import {
  TwoKGoalsEditor,
  TwoKGoalsSummary,
  emptyClassGoalState,
  classGoalsToPayload
} from './TwoKGoalsEditor';

// Code-split: charts only load when the analytics section is expanded.
const AthleteAnalytics = lazy(() => import('./analytics/AthleteAnalytics'));

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Format seconds to MM:SS.s
function formatTime(totalSeconds) {
  if (!totalSeconds) return 'N/A';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = (totalSeconds % 60).toFixed(1);
  return `${minutes}:${seconds.padStart(4, '0')}`;
}

// Format date from MM/DD/YYYY to readable format
function formatDate(dateStr) {
  if (!dateStr) return '';
  const [month, day, year] = dateStr.split('/');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AthleteProfile({ athlete, onBack, onSave, readOnly = false }) {
  const { token, isCoach, isAdmin, user } = useAuth();
  const coachAssignableTeams = user?.teams?.length ? user.teams : (user?.team ? [user.team] : []);
  const [clubRole, setClubRole] = useState(athlete?.user_role || 'athlete');
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    setClubRole(athlete?.user_role || 'athlete');
  }, [athlete?.id, athlete?.user_role]);
  const [editedAthlete, setEditedAthlete] = useState(() => {
    // Normalize field names: ensure ergScore exists even if only erg_score is present
    if (athlete) {
      const normalized = { ...athlete };
      if (normalized.erg_score && !normalized.ergScore) {
        normalized.ergScore = normalized.erg_score;
      }
      normalized.goal2kTime = formatTwoKTime(normalized.goal_2k_seconds);
      normalized.coachGoal2kTime = formatTwoKTime(normalized.coach_goal_2k_seconds);
      return normalized;
    }
    return athlete;
  });
  const [results, setResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [absences, setAbsences] = useState([]);
  const [loadingAbsences, setLoadingAbsences] = useState(false);
  const [gmsStandards, setGmsStandards] = useState({});
  const [isInjured, setIsInjured] = useState(athlete?.is_injured || false);
  const [injuryNotes, setInjuryNotes] = useState(athlete?.injury_notes || '');
  const [updatingInjured, setUpdatingInjured] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [classGoalState, setClassGoalState] = useState(() => emptyClassGoalState(athlete?.two_k_goals));

  // Update editedAthlete and injury status when athlete prop changes
  useEffect(() => {
    if (athlete) {
      const normalized = { ...athlete };
      if (normalized.erg_score && !normalized.ergScore) {
        normalized.ergScore = normalized.erg_score;
      }
      normalized.goal2kTime = formatTwoKTime(normalized.goal_2k_seconds);
      normalized.coachGoal2kTime = formatTwoKTime(normalized.coach_goal_2k_seconds);
      setEditedAthlete(normalized);
      setClassGoalState(emptyClassGoalState(normalized.two_k_goals));
      setIsInjured(athlete.is_injured || false);
      setInjuryNotes(athlete.injury_notes || '');
    }
  }, [athlete]);

  // Fetch athlete results and absences if coach or admin
  useEffect(() => {
    if (athlete && (isCoach() || isAdmin())) {
      fetchAthleteResults();
      fetchAthleteAbsences();
      fetchGmsStandards();
    }
  }, [athlete]);

  const fetchAthleteResults = async () => {
    if (!athlete?.id) return;
    setLoadingResults(true);
    try {
      const response = await fetch(`${API_URL}/api/athlete-results/athlete/${athlete.id}`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
      }
    } catch (err) {
      console.error('Error fetching athlete results:', err);
    } finally {
      setLoadingResults(false);
    }
  };

  const fetchAthleteAbsences = async () => {
    if (!athlete?.id) return;
    setLoadingAbsences(true);
    try {
      const response = await fetch(`${API_URL}/api/attendance/athlete/${athlete.id}`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setAbsences(data || []);
      }
    } catch (err) {
      console.error('Error fetching athlete absences:', err);
    } finally {
      setLoadingAbsences(false);
    }
  };

  const fetchGmsStandards = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gms`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        // Lookup map { canonicalBoatClass: pace_500m }, matching chartUtils' gmsMap shape.
        const standardsMap = {};
        (data.standards || []).forEach(s => {
          const normalizedKey = normalizeBoatClass(s.boat_class);
          const pace = parseFloat(s.pace_500m);
          if (normalizedKey && pace > 0) standardsMap[normalizedKey] = pace;
        });
        setGmsStandards(standardsMap);
      }
    } catch (err) {
      console.error('Error fetching GMS standards:', err);
    }
  };

  // % GMS for a result row via the shared analytics helpers.
  const calculatePercentGMS = (result) => {
    const pct = percentGms(splitFor(result), gmsPaceForRow(result, gmsStandards));
    return pct != null ? Math.round(pct * 10) / 10 : null;
  };

  // Toggle injured status (coach/admin only)
  const handleToggleInjured = async (newInjuredState) => {
    if (!athlete?.id) return;
    setUpdatingInjured(true);
    try {
      const response = await fetch(`${API_URL}/api/athletes/${athlete.id}/injured`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(token),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          isInjured: newInjuredState,
          injuryNotes: newInjuredState ? injuryNotes : null
        })
      });
      if (response.ok) {
        setIsInjured(newInjuredState);
        if (!newInjuredState) {
          setInjuryNotes('');
        }
      } else {
        const data = await response.json();
        console.error('Failed to update injured status:', data.error);
        alert(data.error || 'Failed to update injured status');
      }
    } catch (err) {
      console.error('Error updating injured status:', err);
      alert('Error updating injured status');
    } finally {
      setUpdatingInjured(false);
    }
  };

  if (!athlete) {
    return <p>No athlete selected.</p>;
  }

  const handleSave = () => {
    if (onSave) {
      const normalized = { ...editedAthlete };
      if (normalized.erg_score !== undefined && normalized.ergScore === undefined) {
        normalized.ergScore = normalized.erg_score;
      }
      if (normalized.ergScore !== undefined) {
        delete normalized.erg_score;
      }
      normalized.goal2kSeconds = parseTwoKTime(normalized.goal2kTime);
      delete normalized.goal2kTime;
      if (isCoach() || isAdmin()) {
        normalized.coachGoal2kSeconds = parseTwoKTime(normalized.coachGoal2kTime);
      }
      delete normalized.coachGoal2kTime;
      normalized.twoKGoals = classGoalsToPayload(classGoalState, isCoach() || isAdmin());
      normalized.two_k_goals = normalized.twoKGoals.map((goal) => ({
        boat_class: goal.boatClass,
        goal_2k_seconds: goal.goal2kSeconds,
        coach_goal_2k_seconds: goal.coachGoal2kSeconds,
        effective_goal_2k_seconds: goal.coachGoal2kSeconds || goal.goal2kSeconds
      })).filter((goal) => goal.goal_2k_seconds != null || goal.coach_goal_2k_seconds != null);
      if ((isCoach() || isAdmin()) && normalized.teamsText != null) {
        const teams = String(normalized.teamsText)
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean);
        if (teams.length > 0) {
          normalized.teams = teams;
          normalized.team = normalized.team || teams[0];
        }
        delete normalized.teamsText;
      }
      onSave(normalized);
      setEditMode(false);
    }
  };

  return (
    <div className="p-6">
      <button
        onClick={onBack}
        className="mb-4 text-theme-primary bg-white hover:underline"
      >
        ← Back to Roster
      </button>

      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4 min-w-0">
        <AthleteHeadshot name={athlete.name} headshotUrl={editedAthlete?.headshot_url || athlete.headshot_url} size="lg" />
        <div className="min-w-0">
        <h2 className="text-2xl font-bold">{athlete.name}</h2>
          {athlete.has_user_account && (
            <span className="text-sm text-green-600">
              ✓ Registered {formatRoleLabel(clubRole || athlete.user_role)}
            </span>
          )}
          {(isCoach() || isAdmin()) && athlete.linked_user_id && (clubRole === 'athlete' || clubRole === 'coxswain' || athlete.user_role === 'athlete' || athlete.user_role === 'coxswain') && (
            <div className="mt-2 flex items-center gap-2">
              <label className="text-xs text-gray-600">Club role</label>
              <select
                value={clubRole || athlete.user_role || 'athlete'}
                onChange={async (e) => {
                  const role = e.target.value;
                  const previous = clubRole || athlete.user_role || 'athlete';
                  setClubRole(role);
                  try {
                    const response = await fetch(`${API_URL}/api/athletes/users/${athlete.linked_user_id}/role`, {
                      method: 'PUT',
                      headers: getAuthHeaders(token),
                      body: JSON.stringify({ role })
                    });
                    const data = await response.json().catch(() => ({}));
                    if (!response.ok) {
                      setClubRole(previous);
                      alert(data.error || 'Failed to update role');
                    }
                  } catch (err) {
                    setClubRole(previous);
                    console.error('Error updating role:', err);
                    alert('Failed to update role');
                  }
                }}
                className="border rounded px-2 py-1 text-sm bg-white text-gray-800"
              >
                <option value="athlete">Athlete</option>
                <option value="coxswain">Coxswain</option>
              </select>
            </div>
          )}
          {!athlete.has_user_account && (
            <span className="text-sm text-gray-500">
              No user account (profile only)
            </span>
          )}
        </div>
        </div>
        {!editMode && !readOnly && onSave && (
          <button
            onClick={() => setEditMode(true)}
            className="btn-theme-primary px-4 py-2 rounded-lg"
          >
            Edit Profile
          </button>
        )}
      </div>

      {(isCoach() || isAdmin() || !readOnly) && athlete?.id && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <HeadshotUploader
            athlete={{ ...athlete, headshot_url: editedAthlete?.headshot_url || athlete.headshot_url }}
            onUpdated={(updated) => {
              setEditedAthlete((prev) => ({ ...prev, headshot_url: updated.headshot_url }));
            }}
          />
        </div>
      )}

      {/* Injury Status Section - Coach/Admin only */}
      {(isCoach() || isAdmin()) && (
        <div className={`mb-6 p-4 rounded-lg border ${isInjured ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="injuredStatus"
                  checked={isInjured}
                  onChange={(e) => handleToggleInjured(e.target.checked)}
                  disabled={updatingInjured}
                  className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <label htmlFor="injuredStatus" className={`font-semibold cursor-pointer ${isInjured ? 'text-red-700' : 'text-gray-700'}`}>
                  Mark as Injured
                </label>
              </div>
              {!isInjured && (
                <p className="text-xs text-gray-500">
                  Injured athletes are excluded from lineups but not marked absent.
                </p>
              )}
            </div>
            {isInjured && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-red-700 mb-1">Injury Notes</label>
                  <textarea
                    placeholder="Describe the injury (optional)..."
                    value={injuryNotes}
                    onChange={(e) => setInjuryNotes(e.target.value)}
                    className="w-full p-2 border border-red-200 rounded text-sm resize-none bg-white"
                    rows={2}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    {athlete.injured_at && (
                      <p className="text-xs text-red-600">
                        Marked injured {new Date(athlete.injured_at).toLocaleDateString()}
                        {athlete.injured_by_name && ` by ${athlete.injured_by_name}`}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {injuryNotes !== (athlete.injury_notes || '') && (
                      <button
                        onClick={() => handleToggleInjured(true)}
                        disabled={updatingInjured}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                      >
                        {updatingInjured ? 'Saving...' : 'Save Notes'}
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleInjured(false)}
                      disabled={updatingInjured}
                      className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 disabled:opacity-50"
                    >
                      Clear Injury
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Injury Banner for non-coach/admin view */}
      {!(isCoach() || isAdmin()) && isInjured && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 font-medium">This athlete is currently marked as injured.</p>
          {athlete.injury_notes && (
            <p className="text-sm text-red-600 mt-1">{athlete.injury_notes}</p>
          )}
        </div>
      )}

      {editMode ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { field: 'name', label: 'Name' },
            ...(isAdmin()
              ? [{ field: 'teamsText', label: 'Teams (comma-separated)' }]
              : (!isCoach() ? [{ field: 'team', label: 'Team' }] : [])),
            { field: 'racingclass', label: 'Racing Class' },
            { field: 'email', label: 'Email' },
            { field: 'phone', label: 'Phone' },
            { field: 'height', label: 'Height' },
            { field: 'weight', label: 'Weight' },
            { field: 'ergScore', label: '2k Erg Score', dbField: 'erg_score' },
          ].map(({ field, label, dbField }) => {
            // Use dbField if provided (for snake_case DB fields), otherwise use field
            const actualField = dbField || field;
            const displayValue = field === 'teamsText'
              ? (editedAthlete.teamsText
                ?? (Array.isArray(editedAthlete.teams) ? editedAthlete.teams.join(', ') : (editedAthlete.team || '')))
              : (editedAthlete[actualField] || editedAthlete[field] || '');
            return (
            <div key={field}>
              <label className="block font-semibold mb-1">
                {label}
              </label>
              <input
                type="text"
                  value={displayValue}
                  onChange={(e) => {
                    const updates = { ...editedAthlete };
                    // Update both field names for compatibility
                    updates[field] = e.target.value;
                    if (dbField) {
                      updates[dbField] = e.target.value;
                    }
                    setEditedAthlete(updates);
                  }}
                  className="border p-2 rounded w-full text-theme-primary bg-white"
              />
            </div>
            );
          })}
          <TwoKGoalsEditor
            classGoalState={classGoalState}
            onChange={setClassGoalState}
            canEditCoach={isCoach() || isAdmin()}
            defaultGoalTime={editedAthlete.goal2kTime || ''}
            defaultCoachTime={editedAthlete.coachGoal2kTime || ''}
            onDefaultGoalChange={(value) => setEditedAthlete({ ...editedAthlete, goal2kTime: value })}
            onDefaultCoachChange={(value) => setEditedAthlete({ ...editedAthlete, coachGoal2kTime: value })}
          />
          {isCoach() && !isAdmin() && (
            <div className="md:col-span-2">
              <label className="block font-semibold mb-1">Teams</label>
              <div className="flex flex-wrap gap-3">
                {coachAssignableTeams.map((teamName) => {
                  const selectedTeams = Array.isArray(editedAthlete.teams)
                    ? editedAthlete.teams
                    : (editedAthlete.team ? [editedAthlete.team] : []);
                  const checked = selectedTeams.includes(teamName);
                  return (
                    <label key={teamName} className="inline-flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = new Set(selectedTeams);
                          if (e.target.checked) next.add(teamName);
                          else next.delete(teamName);
                          setEditedAthlete({ ...editedAthlete, teams: [...next] });
                        }}
                        className="w-4 h-4"
                      />
                      {teamName}
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                You can only assign athletes to teams you belong to.
              </p>
            </div>
          )}
          <div className="col-span-2">
            <button
              onClick={handleSave}
              className="btn-theme-primary px-6 py-3 rounded-lg mt-4"
            >
              Save Changes
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <strong>Team{Array.isArray(athlete.teams) && athlete.teams.length > 1 ? 's' : ''}:</strong>{' '}
            {Array.isArray(athlete.teams) && athlete.teams.length > 0
              ? athlete.teams.join(', ')
              : (athlete.team || 'N/A')}
          </div>
          <div>
            <strong>Racing Class:</strong> {athlete.racingclass || 'N/A'}
          </div>
          <div>
            <strong>Email:</strong> {athlete.email || 'N/A'}
          </div>
          <div>
            <strong>Phone:</strong> {athlete.phone || 'N/A'}
          </div>
          <div>
            <strong>Height:</strong> {athlete.height || 'N/A'}
          </div>
          <div>
            <strong>Weight:</strong> {athlete.weight || 'N/A'}
          </div>
          <div>
            <strong>2k Erg Score:</strong> {athlete.erg_score || athlete.ergScore || 'N/A'}
          </div>
          <div className="md:col-span-2">
            <strong>2k Goals:</strong>
            <div className="mt-1">
              <TwoKGoalsSummary
                twoKGoals={athlete.two_k_goals}
                defaultSeconds={athlete.goal_2k_seconds}
                defaultCoachSeconds={athlete.coach_goal_2k_seconds}
              />
            </div>
          </div>
        </div>
      )}

      {/* Results Section - Coach/Admin only */}
      {(isCoach() || isAdmin()) && (
        <div className="mt-8 border-t pt-6">
          <h3 className="text-xl font-bold text-theme-primary mb-4">Performance Results</h3>
          
          {loadingResults ? (
            <p className="text-gray-500">Loading results...</p>
          ) : results.length === 0 ? (
            <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
              <p>No results recorded for this athlete.</p>
              <p className="text-sm mt-1">Add results from the Athlete Results page.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-lg shadow-sm text-sm">
                <thead>
                  <tr className="bg-theme-bg-secondary text-theme-primary text-left">
                    <th className="p-2 font-semibold">Date</th>
                    <th className="p-2 font-semibold">Boat</th>
                    <th className="p-2 font-semibold text-right">Distance</th>
                    <th className="p-2 font-semibold text-right">Time</th>
                    <th className="p-2 font-semibold text-right">Split</th>
                    <th className="p-2 font-semibold text-right">S/R</th>
                    <th className="p-2 font-semibold text-right">% CST</th>
                    <th className="p-2 font-semibold">Prognostic</th>
                  </tr>
                </thead>
                <tbody>
                  {results.slice(0, 10).map((result) => {
                    // Parse interval_details if it's a string (from DB)
                    const intervalDetails = typeof result.interval_details === 'string' 
                      ? JSON.parse(result.interval_details) 
                      : result.interval_details;
                    const hasIntervals = result.interval_count > 1;
                    
                    return (
                      <tr key={result.id} className="border-t hover:bg-gray-50">
                        <td className="p-2">{formatDate(result.result_date)}</td>
                        <td className="p-2">
                          <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                            {result.boat_class}
                          </span>
                        </td>
                        <td className="p-2 text-right font-mono">
                          {result.workout_mode === 'time' ? (
                            // Time-based workout: show time format and total distance
                            <>
                              <span className="text-purple-600">
                                {hasIntervals && intervalDetails?.[0]?.time_seconds 
                                  ? `${result.interval_count}×${Math.floor(intervalDetails[0].time_seconds / 60)}'`
                                  : `${Math.floor((result.total_time_seconds || 0) / 60)}'`
                                }
                              </span>
                              <span className="text-xs text-gray-500 block">{result.distance_meters}m</span>
                            </>
                          ) : hasIntervals ? (
                            // Distance-based intervals
                            <span>{result.interval_count}×{Math.round(result.distance_meters / result.interval_count)}m</span>
                          ) : (
                            <span>{result.distance_meters}m</span>
                          )}
                        </td>
                        <td className="p-2 text-right font-mono font-semibold">
                          {formatTime(result.total_time_seconds)}
                          {hasIntervals && result.interval_rest_seconds && (
                            <span className="text-xs text-gray-400 block">r{result.interval_rest_seconds}s</span>
                          )}
                          {intervalDetails && Array.isArray(intervalDetails) && intervalDetails.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              {intervalDetails.map((interval, idx) => (
                                <div key={idx} className="text-left">
                                  #{idx + 1}: {formatTime(interval.time_seconds)} {interval.stroke_rate ? `@ ${interval.stroke_rate}spm` : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-right font-mono text-gray-600">
                          {formatTime(result.avg_split_seconds)}
                        </td>
                        <td className="p-2 text-right font-mono text-gray-600">
                          {result.avg_stroke_rate ? Math.round(result.avg_stroke_rate) : '-'}
                        </td>
                        <td className="p-2 text-right font-mono">
                          {(() => {
                            const pct = calculatePercentGMS(result);
                            if (!pct) return <span className="text-gray-400">-</span>;
                            const color = pct >= 95 ? 'text-green-600' : 
                                         pct >= 85 ? 'text-blue-600' : 
                                         pct >= 75 ? 'text-yellow-600' : 'text-gray-600';
                            return <span className={`font-semibold ${color}`}>{pct}%</span>;
                          })()}
                        </td>
                        <td className="p-2">
                          <PrognosticBadges result={result} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {results.length > 10 && (
                <p className="text-sm text-gray-500 mt-2 text-center">
                  Showing 10 of {results.length} results. View all in Athlete Results.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Progress & Analytics Section - Coach/Admin only */}
      {(isCoach() || isAdmin()) && (
        <div className="mt-8 border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-theme-primary">Progress &amp; Analytics</h3>
            <button
              type="button"
              onClick={() => setShowAnalytics((prev) => !prev)}
              className="px-3 py-1.5 text-sm font-semibold border border-theme-primary text-theme-primary rounded-lg hover:bg-theme-primary/5 bg-white"
            >
              {showAnalytics ? 'Hide charts' : 'Show charts'}
            </button>
          </div>
          {showAnalytics && (
            <Suspense fallback={<p className="text-gray-500">Loading analytics...</p>}>
              <AthleteAnalytics
                athleteId={athlete.id}
                userId={athlete.linked_user_id || null}
                athleteName={athlete.name}
                embedded
              />
            </Suspense>
          )}
        </div>
      )}

      {/* Absences Section - Coach/Admin only */}
      {(isCoach() || isAdmin()) && (
        <div className="mt-8 border-t pt-6">
          <h3 className="text-xl font-bold text-theme-primary mb-4">
            Absences 
            <span className="text-sm font-normal text-gray-500 ml-2">
              ({absences.length} total)
            </span>
          </h3>
          
          {loadingAbsences ? (
            <p className="text-gray-500">Loading absences...</p>
          ) : absences.length === 0 ? (
            <div className="bg-green-50 p-4 rounded-lg text-center text-green-700">
              <p>No absences recorded for this athlete. 🎉</p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-64">
              <table className="w-full border-collapse bg-white rounded-lg shadow-sm text-sm">
                <thead className="sticky top-0 bg-theme-bg-secondary">
                  <tr className="text-theme-primary text-left">
                    <th className="p-2 font-semibold">Date</th>
                    <th className="p-2 font-semibold">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {absences.map((absence, index) => (
                    <tr key={absence.id || index} className="border-t hover:bg-gray-50">
                      <td className="p-2 font-medium">{formatDate(absence.absence_date)}</td>
                      <td className="p-2 text-gray-600">{absence.reason || 'No reason provided'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
